import { useQuery } from '@tanstack/react-query'
import { Box, Flex, Text } from '@workwhile/ui'
import React from 'react'

interface TranscriptItem {
  role: 'user' | 'agent'
  content: string
  words: {
    start: number
    end: number
    word: string
  }[]
}

const useTranscript = (transcriptUrl: string) => {
  return useQuery<TranscriptItem>({
    queryKey: ['transcript', transcriptUrl],
    enabled: Boolean(transcriptUrl),
    queryFn: async () => {
      const response = await fetch(transcriptUrl, { method: 'GET' })
      if (!response.ok) {
        throw new Error('Failed to fetch transcript')
      }
      return response.json()
    },
  })
}

interface TranscriptProps {
  transcriptUrl: string
  setSeekTime: (time: number) => void
}

export const Transcript = ({ transcriptUrl, setSeekTime }: TranscriptProps) => {
  const { data, error, isLoading } = useTranscript(transcriptUrl)

  if (isLoading) {
    return null
  }

  // fallback to display the file itself in case we have trouble parsing the transcript
  if ((!isLoading && error) || !data) {
    return <iframe src={transcriptUrl} width="100%" height="100%" />
  }

  return (
    <>
      {Object.values(data)?.map((item, index) => (
        <Flex
          key={index}
          onClick={() => setSeekTime(item.words[0].start)}
          justifyContent={item.role === 'user' ? 'flex-end' : 'flex-start'}
        >
          <Box
            width="fit-content"
            borderRadius="standard"
            backgroundColor={item.role === 'user' ? 'primary50' : 'offWhite50'}
            p={2}
            mt={2}
            ml={item.role === 'user' ? 4 : 0}
            mr={item.role === 'user' ? 0 : 4}
          >
            <Text
              color="body"
              textAlign={item.role === 'user' ? 'right' : 'left'}
            >
              {item.content}
            </Text>
          </Box>
        </Flex>
      ))}
    </>
  )
}
